import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { Subject, takeUntil } from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { IOrderLineAlert } from '../../models/quotations.models';
import { IParseProduct } from '../../models/quotations.models';
import { ToggleOrderLineAlert, ToggleOrderLineAlertSuccess, IgnoreAllAlerts, IgnoreAllAlertsSuccess } from '../../store/quotations.actions';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { TranslateService } from '@ngx-translate/core';

export interface OrderLineAlertsData {
  product: IParseProduct;
  alerts: IOrderLineAlert[];
  baseUrl: string;
  quotationId: number;
}

@Component({
  selector: 'app-order-line-alerts',
  templateUrl: './order-line-alerts.component.html',
  styleUrls: ['./order-line-alerts.component.scss']
})
export class OrderLineAlertsComponent implements OnInit, OnDestroy {

  isIgnoredExpanded: boolean = false;
  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<OrderLineAlertsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: OrderLineAlertsData,
    private store: Store<AppState>,
    private actions$: Actions,
    private dialog: MatDialog,
    private translate: TranslateService,
  ) { }

  ngOnInit(): void {
    this.actions$.pipe(
      ofType(ToggleOrderLineAlertSuccess),
      takeUntil(this.destroy$)
    ).subscribe(action => {
      const idx = this.data.alerts.findIndex(a => a.id === action.alert.id);
      if (idx !== -1) {
        this.data.alerts = [
          ...this.data.alerts.slice(0, idx),
          action.alert,
          ...this.data.alerts.slice(idx + 1)
        ];
      }
    });

    this.actions$.pipe(
      ofType(IgnoreAllAlertsSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.dialogRef.close();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get activeAlerts(): IOrderLineAlert[] {
    return this.data.alerts.filter(a => a.state !== 'ignored');
  }

  get ignoredAlerts(): IOrderLineAlert[] {
    return this.data.alerts.filter(a => a.state === 'ignored');
  }

  toggleAlert(alertId: number): void {
    this.store.dispatch(ToggleOrderLineAlert({
      quotationId: this.data.quotationId,
      orderLineId: this.data.product.order_line_id!,
      alertId
    }));
  }

  ignoreAll(): void {
    const openAlertIds = this.activeAlerts.map(a => a.id);
    if (openAlertIds.length === 0) return;
    this.store.dispatch(IgnoreAllAlerts({
      quotationId: this.data.quotationId,
      alertIds: openAlertIds
    }));
  }

  removeProduct(): void {
    const confirmRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        title: this.translate.instant('quotations_and_orders.remove_product'),
        text: this.translate.instant('quotations_and_orders.remove_product_confirmation'),
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translate.instant('common.remove'),
        confirm_class: 'btn-danger',
      }
    });
    confirmRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.dialogRef.close({ action: 'delete' });
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  fix(): void {
    this.dialogRef.close(true);
  }

  placeholder = 'assets/icones/default-image-product.svg';
}
